import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import axios from 'axios';

interface HOA {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'RESIDENT' | 'VENDOR' | 'PLATFORM_ADMIN';
  hoaId?: string;
  propertyGroupId?: string;
  hoa: HOA;
  token: string;
  mustResetPassword?: boolean;
  isEmailVerified?: boolean;
  
  // Vendor-specific fields
  companyName?: string;
  serviceType?: string;
  contactNumber?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthLoading: boolean;
  login: (email: string, password: string, hoaId?: string) => Promise<User>;
  setUserData: (user: User, token: string) => void;
  logout: () => void;
  selectHOA: (hoaId: string) => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const HOA_RESET_MINUTES = 5;
const HOA_LOGOUT_KEY = 'admin_hoa_logout';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const validateAndRestoreSession = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (!storedUser) {
        setIsAuthLoading(false);
        return;
      }

      let parsedUser: any;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('user');
        setIsAuthLoading(false);
        return;
      }

      const token = parsedUser.token || storedToken;

      if (!token) {
        // No token at all — can't be authenticated
        localStorage.removeItem('user');
        setIsAuthLoading(false);
        return;
      }

      try {
        // Validate the stored token against the server; get fresh user data
        const response = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Token valid — merge fresh server data with stored data (keeps token + hoaId etc.)
        const freshUser = { ...parsedUser, ...(response.data as object), token };

        // HOA reset logic for ADMINs
        if (freshUser.role === 'ADMIN') {
          const lastLogout = localStorage.getItem(HOA_LOGOUT_KEY);
          if (lastLogout) {
            const diff = Date.now() - parseInt(lastLogout, 10);
            if (diff > HOA_RESET_MINUTES * 60 * 1000) {
              delete freshUser.hoaId;
            }
          }
        }

        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          // Token expired or invalid — clear session completely
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('selectedPropertyGroup');
          localStorage.removeItem('selectedPropertyGroupSlug');
        } else {
          // Network/server error — restore from localStorage so the app
          // remains usable during brief outages, but don't update stored data
          if (parsedUser.role === 'ADMIN') {
            const lastLogout = localStorage.getItem(HOA_LOGOUT_KEY);
            if (lastLogout) {
              const diff = Date.now() - parseInt(lastLogout, 10);
              if (diff > HOA_RESET_MINUTES * 60 * 1000) {
                delete parsedUser.hoaId;
                localStorage.setItem('user', JSON.stringify(parsedUser));
              }
            }
          }
          if (parsedUser.role === 'PLATFORM_ADMIN' && storedToken && !parsedUser.token) {
            parsedUser.token = storedToken;
            localStorage.setItem('user', JSON.stringify(parsedUser));
          }
          setUser(parsedUser);
        }
      } finally {
        setIsAuthLoading(false);
      }
    };

    validateAndRestoreSession();
  }, []);

  const login = async (email: string, password: string, hoaId?: string): Promise<User> => {
    try {
      const response = await axios.post<User>('/api/auth/login', { 
        email, 
        password,
        ...(hoaId && { hoaId }) 
      });
      const userData = response.data;
      // If admin, check HOA reset logic
      if (userData.role === 'ADMIN') {
        const lastLogout = localStorage.getItem(HOA_LOGOUT_KEY);
        if (lastLogout) {
          const diff = Date.now() - parseInt(lastLogout, 10);
          if (diff > HOA_RESET_MINUTES * 60 * 1000) {
            // More than 5 min since logout, clear HOA
            delete userData.hoaId;
          }
        }
      }
      // Store user data in local storage
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    // Remove user data from local storage
    if (user?.role === 'ADMIN') {
      localStorage.setItem(HOA_LOGOUT_KEY, Date.now().toString());
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  const setUserData = (user: User, token: string) => {
    // Include token in user object for platform admin authentication
    const userWithToken = { ...user, token };
    localStorage.setItem('user', JSON.stringify(userWithToken));
    localStorage.setItem('token', token);
    setUser(userWithToken);
  };

  const selectHOA = (hoaId: string) => {
    if (user) {
      const updatedUser = { ...user, hoaId };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthLoading, login, setUserData, logout, selectHOA, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 